import { openModal } from '@/lib/modal';
import { createPunch, updatePunch, deletePunch } from '@/lib/data/punch';
import { emptyToNull } from '@/lib/form-values';
import { PUNCH_NO_ROOM } from '@/lib/punch';
import type { PunchItemRow, RoomRow, DesignerContactRow } from '@/types/database';

/**
 * Shared punch create/edit modal, on the same two-mode shape as
 * `schedule-modal.ts`:
 *
 *   1. Room fixed — pass `roomId` and no `rooms`; no Room field is rendered.
 *      (The room page's Punch tab.)
 *   2. Room picker — pass `rooms`, `projectId` and no `roomId`; a Room
 *      dropdown leads, "No room" included. (The project-level Punch List.)
 *
 * When editing, the item's own room is the default in both modes.
 *
 * 0048 — the picker also offers "No room", for work that belongs to the whole
 * house rather than one space. That needs a `projectId`, because nothing else
 * says which project a room-less item belongs to.
 *
 * Fixed in passing: the picker was RENDERED when editing but never written —
 * `onSubmit` sent no `room_id` for an existing item, so changing the dropdown
 * silently did nothing. Harmless while every item had a room and the field was
 * mostly cosmetic; the moment "No room" became a choice it would have been a
 * control that plainly doesn't work. The room page's mode still never writes
 * it: there the room is fixed by definition.
 *
 * The vendor is a contact (0040). `assignee` — free text from before that —
 * is still written back untouched, so an older item keeps whatever was typed
 * in it until someone picks a contact.
 */
export function openPunchModal(opts: {
  roomId?: string;
  rooms?: RoomRow[];
  /** Required with `rooms`: where a "No room" item belongs (0048). */
  projectId?: string;
  sortOrder: number;
  punch?: PunchItemRow;
  /** Contacts offered as the vendor. Usually the project's. */
  contacts?: DesignerContactRow[];
  onDone: () => void;
}): void {
  const p = opts.punch;
  const { rooms, contacts = [] } = opts;
  const usePicker = !opts.roomId && !!rooms;
  // `p?.room_id ?? opts.roomId ?? rooms?.[0]?.id` was wrong the moment room_id
  // became nullable: `null ?? x` falls through, so an item already in NO room
  // opened showing the first room — and saving would have silently moved it
  // there. When editing, the item's own room IS the answer, null included.
  const initialRoomId = p ? p.room_id ?? '' : opts.roomId ?? rooms?.[0]?.id ?? '';

  openModal({
    title: p ? 'Edit punch item' : 'Add punch item',
    showDelete: !!p,
    fields: [
      { name: 'description', label: 'List item', type: 'textarea', voice: true, voiceKind: 'line',
        required: true, defaultValue: p?.description },
      ...(usePicker ? [{
        name: 'room_id', label: 'Room', type: 'select' as const,
        // Not `required`: an empty value is now a real answer, and requiring
        // it would refuse the one this field was changed to allow.
        defaultValue: initialRoomId,
        options: [
          { value: '', label: PUNCH_NO_ROOM },
          ...(rooms ?? []).map((r) => ({ value: r.id, label: r.name })),
        ],
      }] : []),
      {
        name: 'designer_contact_id', label: 'Vendor assigned to', type: 'select' as const,
        options: [
          { value: '', label: contacts.length === 0 ? '— No contacts on this project —' : '— Nobody yet —' },
          ...contacts.map((c) => ({ value: c.id, label: c.company ? `${c.name} — ${c.company}` : c.name })),
        ],
        defaultValue: p?.designer_contact_id ?? '',
      },
      { name: 'due_date', label: 'Due date', type: 'date' as const, defaultValue: p?.due_date },
      { name: 'complete', label: 'Complete', type: 'checkbox' as const, defaultValue: p?.complete ?? false },
    ],
    onSubmit: async (v) => {
      const payload = {
        description: String(v.description),
        designer_contact_id: emptyToNull(v.designer_contact_id),
        due_date: emptyToNull(v.due_date),
        complete: !!v.complete,
      };
      // Null when "No room" was chosen; undefined in the room page's mode,
      // where the field isn't rendered. Both use sites below ask `usePicker`
      // first, so the room page never writes a room — there it is fixed, and
      // sending it would let an edit move the item out of the list it's in.
      const pickedRoom = emptyToNull(v.room_id);
      if (p) {
        // No project_id on an update: the row already has one, and the trigger
        // only asks for it when a room-less item has none. Sending it would be
        // a second opinion about a question already answered.
        await updatePunch(p.id, usePicker ? { ...payload, room_id: pickedRoom ?? null } : payload);
      } else {
        const targetRoom = usePicker ? pickedRoom : opts.roomId ?? null;
        await createPunch({
          room_id: targetRoom,
          // Ignored by the database when there IS a room — the trigger takes
          // the project from the room — and the only answer when there isn't.
          project_id: opts.projectId,
          sort_order: opts.sortOrder,
          ...payload,
        });
      }
      opts.onDone();
    },
    onDelete: p ? async () => { await deletePunch(p.id); opts.onDone(); } : undefined,
  });
}
